import Fuse from 'fuse.js';

import type { RegisterMenuItemMatch } from '@atlaskit/editor-ui-control-model/types';

type QuickInsertSearchData = {
	description?: string;
	keywords?: string[];
	shortcut?: string;
	title: string;
};

type FormatMessage = Parameters<RegisterMenuItemMatch>[0]['formatMessage'];

const options = {
	includeScore: true,
	keys: [
		{ name: 'title', weight: 0.57 },
		{ name: 'keywords', weight: 0.08 },
		{ name: 'description', weight: 0.04 },
		{ name: 'shortcut', weight: 0.01 },
	],
	threshold: 0.3,
};

const normalizeScore = (score: number | undefined): number => Math.min(1, Math.max(0, score ?? 1));

/** Creates a cached matcher using consumer-formatted Quick Insert search data. */
export const createQuickInsertMatcher = (
	getSearchData: (context: { formatMessage: FormatMessage }) => QuickInsertSearchData,
): RegisterMenuItemMatch => {
	let cachedFormatMessage: FormatMessage | undefined;
	let fuse: Fuse<QuickInsertSearchData> | undefined;

	return ({ formatMessage, query }) => {
		const normalizedQuery = query.trim();
		if (!normalizedQuery) {
			return { score: 0 };
		}

		if (!fuse || cachedFormatMessage !== formatMessage) {
			fuse = new Fuse([getSearchData({ formatMessage })], options);
			cachedFormatMessage = formatMessage;
		}

		const result = fuse.search(normalizedQuery)[0];
		return result ? { score: normalizeScore(result.score) } : null;
	};
};
